import { useMemo, useState, type ReactElement } from 'react';
import {
  useDisciplineEvolution,
  type EvolutionWeek,
} from '@/lib/analytics';
import { useProfile } from '@/lib/auth';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';
import type { Discipline } from '@/lib/workouts';

/**
 * Tab "Evolución por disciplina".
 *
 * Selectores:
 *   - Discipline: swim/bike/run/strength.
 *   - Rango: 30 / 90 / 365 días.
 *
 * Mini-charts SVG en orden vertical. Cada uno tiene tooltip hover y se
 * adapta a la métrica disponible para la disciplina:
 *   - Volumen semanal (horas).
 *   - TSS semanal.
 *   - Distancia semanal (cuando aplica).
 *   - Pace medio (running) o Power medio (bike) — con línea de referencia
 *     (FTP del profile en el caso de power).
 *   - HR medio — con línea horizontal de LTHR del profile.
 */

const DISCIPLINES_TAB: Discipline[] = ['swim', 'bike', 'run', 'strength'];
const RANGE_OPTIONS = [
  { id: 30, label: '30 días' },
  { id: 90, label: '90 días' },
  { id: 365, label: '365 días' },
] as const;

export function EvolutionByDiscipline(): ReactElement {
  const [discipline, setDiscipline] = useState<Discipline>('bike');
  const [days, setDays] = useState<number>(90);
  const query = useDisciplineEvolution(discipline, days);
  const profileQuery = useProfile();
  const ftpW = profileQuery.data?.ftpW ?? null;
  const lthrBpm = profileQuery.data?.lthrBpm ?? null;

  return (
    <article className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Disciplina">
          {DISCIPLINES_TAB.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDiscipline(d)}
              aria-pressed={discipline === d}
              className={
                discipline === d
                  ? 'rounded-md bg-teal-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1'
                  : 'rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1'
              }
            >
              {disciplineLabel(d)}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Rango">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setDays(r.id)}
              aria-pressed={days === r.id}
              className={
                days === r.id
                  ? 'rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-1'
                  : 'rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-1'
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-sm text-slate-600">
          Cargando evolución…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="text-sm text-rose-700">
          No se pudo cargar la evolución.
        </p>
      )}

      {query.data && query.data.weeks.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
          Sin entrenos en el rango seleccionado.
        </p>
      )}

      {query.data && query.data.weeks.length > 0 && (
        <div className="space-y-4">
          <BarChartCard
            title="Volumen semanal (horas)"
            unit="h"
            weeks={query.data.weeks}
            getValue={(w) => w.hours}
            color="#0d9488"
          />
          <BarChartCard
            title="TSS semanal"
            unit=""
            weeks={query.data.weeks}
            getValue={(w) => w.tss}
            color="#7c3aed"
          />
          {(discipline === 'swim' || discipline === 'bike' || discipline === 'run' || discipline === 'brick') && (
            <BarChartCard
              title="Distancia semanal (km)"
              unit="km"
              weeks={query.data.weeks}
              getValue={(w) => w.distanceM / 1000}
              color="#0369a1"
            />
          )}
          {(discipline === 'run' || discipline === 'brick') && (
            <LineChartCard
              title="Pace medio running (min/km)"
              weeks={query.data.weeks}
              getValue={(w) => (w.avgPaceSecPerKm !== null ? w.avgPaceSecPerKm / 60 : null)}
              color="#059669"
              valueFormatter={(v) => formatPace(v * 60)}
              invertY
            />
          )}
          {(discipline === 'bike' || discipline === 'brick') && (
            <LineChartCard
              title="Power medio bike (W)"
              weeks={query.data.weeks}
              getValue={(w) => w.avgPowerW}
              color="#c2410c"
              valueFormatter={(v) => `${Math.round(v)} W`}
              referenceLine={ftpW ?? null}
              referenceLabel="FTP"
            />
          )}
          <LineChartCard
            title="HR medio (bpm)"
            weeks={query.data.weeks}
            getValue={(w) => w.avgHr}
            color="#be123c"
            valueFormatter={(v) => `${Math.round(v)} bpm`}
            referenceLine={lthrBpm ?? null}
            referenceLabel="LTHR"
          />
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------
// Mini-charts SVG inline
// ---------------------------------------------------------------------------

const W = 720;
const H = 180;
const M = { top: 20, right: 16, bottom: 28, left: 40 };

function BarChartCard({
  title,
  unit,
  weeks,
  getValue,
  color,
}: {
  title: string;
  unit: string;
  weeks: EvolutionWeek[];
  getValue: (w: EvolutionWeek) => number;
  color: string;
}): ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const values = weeks.map(getValue);
  const max = Math.max(1, ...values);
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const n = weeks.length;
  const barW = Math.max(2, Math.min(20, plotW / Math.max(n, 1) - 2));

  function xOf(i: number): number {
    if (n <= 1) return M.left + plotW / 2;
    return M.left + (i / Math.max(n - 1, 1)) * plotW;
  }

  function yOf(v: number): number {
    const r = Math.max(0, Math.min(1, v / max));
    return M.top + plotH - r * plotH;
  }

  const ariaSummary = useMemo(() => {
    if (values.length === 0) return 'Sin datos';
    const last = values[values.length - 1] ?? 0;
    return `Última semana: ${formatNumber(last)} ${unit}. Máximo: ${formatNumber(max)} ${unit}.`;
  }, [values, max, unit]);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500">{ariaSummary}</p>
      <svg
        role="img"
        aria-label={`${title}: ${ariaSummary}`}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="mt-2 w-full"
      >
        <rect x={M.left} y={M.top} width={plotW} height={plotH} fill="#ffffff" stroke="#e2e8f0" />
        {/* eje Y dos ticks */}
        {[0, max / 2, max].map((t, i) => (
          <g key={i}>
            <line
              x1={M.left}
              x2={M.left + plotW}
              y1={yOf(t)}
              y2={yOf(t)}
              stroke="#e2e8f0"
              strokeDasharray="2 4"
            />
            <text
              x={M.left - 6}
              y={yOf(t) + 3}
              fontSize={10}
              fill="#475569"
              textAnchor="end"
            >
              {formatNumber(t)}
            </text>
          </g>
        ))}

        {weeks.map((w, i) => {
          const v = getValue(w);
          const y = yOf(v);
          const x = xOf(i) - barW / 2;
          const h = Math.max(0, M.top + plotH - y);
          return (
            <rect
              key={w.weekStartISO}
              x={x}
              y={y}
              width={barW}
              height={h}
              fill={hover === i ? '#0f172a' : color}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              role="presentation"
            />
          );
        })}

        {hover !== null && weeks[hover] && (
          <Tooltip
            x={xOf(hover)}
            label={`${formatShortWeek(weeks[hover].weekStartISO)}: ${formatNumber(values[hover] ?? 0)} ${unit}`}
            plotW={plotW}
            plotLeft={M.left}
          />
        )}
      </svg>
    </section>
  );
}

function LineChartCard({
  title,
  weeks,
  getValue,
  color,
  valueFormatter,
  referenceLine,
  referenceLabel,
  invertY,
}: {
  title: string;
  weeks: EvolutionWeek[];
  getValue: (w: EvolutionWeek) => number | null;
  color: string;
  valueFormatter: (v: number) => string;
  referenceLine?: number | null;
  referenceLabel?: string;
  invertY?: boolean;
}): ReactElement {
  const [hover, setHover] = useState<number | null>(null);
  const values = weeks.map(getValue);
  const valid = values.filter((v): v is number => v !== null);
  const min = valid.length > 0 ? Math.min(...valid, referenceLine ?? Infinity) : 0;
  const max = valid.length > 0 ? Math.max(...valid, referenceLine ?? -Infinity) : 1;
  const range = Math.max(1, max - min);
  const plotW = W - M.left - M.right;
  const plotH = H - M.top - M.bottom;
  const n = weeks.length;

  function xOf(i: number): number {
    if (n <= 1) return M.left + plotW / 2;
    return M.left + (i / Math.max(n - 1, 1)) * plotW;
  }

  function yOf(v: number): number {
    const ratio = (v - min) / range;
    return invertY ? M.top + ratio * plotH : M.top + plotH - ratio * plotH;
  }

  // Path con saltos en valores null.
  const pathParts: string[] = [];
  let started = false;
  for (let i = 0; i < weeks.length; i++) {
    const v = values[i];
    if (v === null || v === undefined) {
      started = false;
      continue;
    }
    pathParts.push(`${started ? 'L' : 'M'}${xOf(i).toFixed(2)},${yOf(v).toFixed(2)}`);
    started = true;
  }
  const path = pathParts.join(' ');

  const ariaSummary = useMemo(() => {
    if (valid.length === 0) return 'Sin datos';
    const last = values[values.length - 1];
    return `Última semana: ${last !== null && last !== undefined ? valueFormatter(last) : 's/d'}.`;
  }, [valid, values, valueFormatter]);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500">{ariaSummary}</p>
      <svg
        role="img"
        aria-label={`${title}: ${ariaSummary}`}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="mt-2 w-full"
      >
        <rect x={M.left} y={M.top} width={plotW} height={plotH} fill="#ffffff" stroke="#e2e8f0" />

        {/* ticks Y */}
        {[min, (min + max) / 2, max].map((t, i) => (
          <g key={i}>
            <line
              x1={M.left}
              x2={M.left + plotW}
              y1={yOf(t)}
              y2={yOf(t)}
              stroke="#e2e8f0"
              strokeDasharray="2 4"
            />
            <text
              x={M.left - 6}
              y={yOf(t) + 3}
              fontSize={10}
              fill="#475569"
              textAnchor="end"
            >
              {valueFormatter(t).replace(/\s\S+$/, '')}
            </text>
          </g>
        ))}

        {/* línea de referencia (FTP/LTHR) */}
        {typeof referenceLine === 'number' && referenceLine > 0 && (
          <g>
            <line
              x1={M.left}
              x2={M.left + plotW}
              y1={yOf(referenceLine)}
              y2={yOf(referenceLine)}
              stroke="#475569"
              strokeWidth={1.5}
              strokeDasharray="6 3"
            />
            <text
              x={M.left + plotW - 4}
              y={yOf(referenceLine) - 4}
              fontSize={10}
              fill="#475569"
              textAnchor="end"
            >
              {referenceLabel ?? ''}: {valueFormatter(referenceLine)}
            </text>
          </g>
        )}

        {path && (
          <path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        )}

        {/* puntos */}
        {weeks.map((w, i) => {
          const v = values[i];
          if (v === null || v === undefined) return null;
          return (
            <circle
              key={w.weekStartISO}
              cx={xOf(i)}
              cy={yOf(v)}
              r={hover === i ? 4 : 2.5}
              fill={hover === i ? '#0f172a' : color}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}

        {hover !== null && weeks[hover] && values[hover] !== null && values[hover] !== undefined && (
          <Tooltip
            x={xOf(hover)}
            label={`${formatShortWeek(weeks[hover].weekStartISO)}: ${valueFormatter(values[hover] as number)}`}
            plotW={plotW}
            plotLeft={M.left}
          />
        )}
      </svg>
    </section>
  );
}

function Tooltip({
  x,
  label,
  plotW,
  plotLeft,
}: {
  x: number;
  label: string;
  plotW: number;
  plotLeft: number;
}): ReactElement {
  const boxW = 180;
  const boxH = 28;
  let bx = x + 8;
  if (bx + boxW > plotLeft + plotW) bx = x - boxW - 8;
  const by = M.top + 4;
  return (
    <g pointerEvents="none">
      <line
        x1={x}
        x2={x}
        y1={M.top}
        y2={H - M.bottom}
        stroke="rgb(240 240 245)"
        strokeDasharray="2 2"
      />
      <rect x={bx} y={by} width={boxW} height={boxH} rx={4} fill="rgb(240 240 245)" fillOpacity={0.92} />
      <text x={bx + 8} y={by + 18} fontSize={11} fill="#f8fafc">
        {label}
      </text>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatNumber(n: number): string {
  if (n >= 100) return Math.round(n).toString();
  if (n >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

function formatPace(secPerKm: number): string {
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm - m * 60);
  return `${m}:${String(s).padStart(2, '0')}/km`;
}

function formatShortWeek(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
